import { Box } from '@dtx-company/design-system/src/components/Box/Box'
import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react'
import { Divider } from '@dtx-company/design-system/src/components/Divider/Divider'
import { ModalTypes } from '@dtx-company/inter-app/src/redux/slices/modal'
import { Product, Template } from '@dtx-company/flow-codegen/src/code/generated.types'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { Stack } from '@dtx-company/design-system/src'
import { Typography } from '@dtx-company/design-system/src/components/Typography/Typography'
import { getTemplatePreviewURL } from '../DesignHuddle/utils'
import {
  setProductId,
  setTemplateId
} from '@dtx-company/inter-app/src/redux/slices/printStoreApiSlice/printStoreSlice'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useDesignHuddle } from '../DesignHuddle/hooks'
import { useDispatch } from 'react-redux'
import { useModal } from '@dtx-company/inter-app/src/redux/slices/utils/modal'
import { useRouter } from 'next/router'
import Image from 'next/image'
import LegacyImage from 'next/legacy/image'
import Link from 'next/link'
import events from '@dtx-company/inter-app/src/event-tracking/events/templatesStore'

export interface TemplateCardProps {
  template: Template
  loggedOutHandler?: Dispatch<SetStateAction<string>>
  productId?: Product['id']
  productName?: string
  editorLinkEvent?: () => void
  signUpEvent?: () => void
  onClickAuthedCard?: () => void
  unified?: boolean
}

interface TemplateCardContentProps extends Pick<TemplateCardProps, 'productId' | 'productName'> {
  previewUrl: string | undefined
  templateName: Template['name']
  aspectRatio: number
}

const TemplateCardContent: FC<TemplateCardContentProps> = ({
  previewUrl,
  templateName,
  aspectRatio,
  productName
}) => {
  return (
    <Box
      role="group"
      aria-label="product"
      sx={theme => ({
        width: '100%',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        justifySelf: { xs: 'center', md: 'initial' },
        cursor: 'pointer',
        border: 1,
        borderColor: 'border.outline',
        borderRadius: theme.spacing(2),
        backgroundColor: 'surface.0',
        '&:hover': {
          boxShadow: '3px 3px 30px 0px rgba(17, 17, 17, 0.1)'
        },
        overflow: 'hidden',
        boxSizing: 'border-box',
        '.MuiDivider-root': {
          borderColor: 'border.outline'
        }
      })}
    >
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            position: 'relative',
            height: 0,
            paddingTop: `${(1 / aspectRatio) * 100}%`,
            overflow: 'hidden',
            backgroundColor: 'surface.disabled',
            '& > img': {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 1,
              borderColor: 'border.outline',
              boxSizing: 'border-box'
            }
          }}
        >
          {previewUrl && (
            <Image
              key={previewUrl}
              src={previewUrl}
              width={320}
              height={320 / aspectRatio}
              /*
               * Use empty alt text since we're showing `template.name` in the same card.
               * If we set this alt text to `template.name` as well, screen readers would read
               * it twice when focusing on this template card.
               */
              alt=""
            />
          )}
        </Box>
      </Box>
      <Divider />
      <Box
        sx={theme => ({
          margin: theme.spacing(4, 5),
          textAlign: 'left',
          '#product-name': {
            mt: theme.spacing(0.75)
          }
        })}
      >
        <Typography component={'p'} variant="bodySmall">
          {templateName}
        </Typography>
        {productName && (
          <Typography
            component={'p'}
            variant="bodySmall"
            color={'text.secondary'}
            id="product-name"
          >
            {productName}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

const UnifiedTemplatesCardContent: FC<TemplateCardContentProps> = ({
  previewUrl,
  templateName,
  aspectRatio,
  productName
}) => {
  return (
    <Stack sx={{ width: 256 * aspectRatio }}>
      <Box
        sx={theme => ({
          height: 256,
          position: 'relative',
          mb: theme.spacing(3),
          border: `1px solid ${theme.palette.border.divider}`
        })}
      >
        {previewUrl && <LegacyImage src={`${previewUrl}&w=800&q=75`} alt="" layout="fill" />}
      </Box>
      <Box
        sx={{
          '& p': {
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            display: { xs: 'none', sm: '-webkit-box' },
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            textAlign: 'left'
          }
        }}
      >
        <Typography variant="labelLarge" component={'p'}>
          {templateName}
        </Typography>
      </Box>
      {productName && (
        <Box
          sx={{
            mt: theme => theme.spacing(1),
            '& p': {
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 3,
              display: '-webkit-box',
              textAlign: 'left'
            }
          }}
        >
          <Typography variant="labelSmall" color="text.secondary" component={'p'}>
            {productName}
          </Typography>
        </Box>
      )}
    </Stack>
  )
}

const buildRedirectPath = (
  basePath: string,
  templateId: string,
  queries: Record<string, string | string[] | undefined>
): string => {
  const redirectPath = `${basePath}/${templateId}?`
  const params = Object.keys(queries).reduce((acc, key, idx, array) => {
    if (key !== 'templateId' && queries[key]) {
      return acc + `${key}=${queries[key]}${idx !== array.length - 1 ? '&' : ''}`
    }
    return acc
  }, '')
  return redirectPath + params
}

export const TemplateCard: FC<TemplateCardProps> = ({
  template,
  loggedOutHandler,
  productId,
  productName,
  editorLinkEvent,
  signUpEvent,
  onClickAuthedCard,
  unified = false
}) => {
  const router = useRouter()
  const { batchId, productId: routerProductId, templateCategoryId } = router.query
  const { brandKitTco, dhLibIsReady } = useDesignHuddle()
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined)
  const dispatch = useDispatch()
  const { isAuthenticated } = useAuthState()
  const { setOpen: openSignUpModal } = useModal(ModalTypes.TEMPLATES_SIGN_UP_MODAL)
  const templateProductId = productId || routerProductId

  useEffect(() => {
    if (dhLibIsReady) {
      const updatePreviewImage = async (): Promise<void> => {
        const imageUrl = await getTemplatePreviewURL(template.dhTemplateId, brandKitTco)
        setPreviewUrl(imageUrl)
      }
      updatePreviewImage()
    }
  }, [dhLibIsReady, brandKitTco, template.dhTemplateId])

  const onClick = (): void => {
    if (templateProductId && template.dhTemplateId) {
      dispatch(setProductId(templateProductId as string))
      dispatch(setTemplateId(template.dhTemplateId))
    }
    editorLinkEvent
      ? editorLinkEvent()
      : events.Templates_Clicked_TemplateCard({
          templateName: template.name,
          templateId: template?.dhTemplateId
        })
  }

  const aspectRatio = template.width / template.height
  const nextLinkQuery = {
    templateId: template.templateId,
    dhTemplateId: template.dhTemplateId,
    ...(templateProductId && { productId: templateProductId }),
    ...(templateCategoryId && { templateCategoryId }),
    ...(batchId && { batchId })
  }

  const redirectPath = buildRedirectPath(
    '/store/templates/edit',
    template.templateId,
    nextLinkQuery
  )

  const ContentComponent = useMemo(
    () => (unified ? UnifiedTemplatesCardContent : TemplateCardContent),
    [unified]
  )

  return (
    <>
      {isAuthenticated ? (
        <Link
          href={{
            pathname: Routes.PRINT_STORE_EDIT_TEMPLATE,
            query: nextLinkQuery
          }}
          onClick={() => {
            onClick()
            onClickAuthedCard?.()
          }}
          data-testid={`Template: ${template.name}`}
        >
          <ContentComponent
            previewUrl={previewUrl}
            templateName={template.name}
            aspectRatio={aspectRatio}
            productName={productName}
          />
        </Link>
      ) : (
        <Box
          component="button"
          sx={theme => ({
            appearance: 'none',
            backgroundColor: 'unset',
            border: 'none',
            padding: 0,
            width: '100%',
            display: 'inline',
            borderRadius: theme.spacing(3)
          })}
          onClick={() => {
            signUpEvent
              ? signUpEvent()
              : events.PrintTemplates_TemplateStore_Viewed_SignUpModal({
                  templateCategoryId: templateCategoryId ? String(templateCategoryId) : '',
                  productId: templateProductId ? String(templateProductId) : ''
                })
            loggedOutHandler?.(redirectPath)
            openSignUpModal()
          }}
          data-testid={`${template.name}-signup`}
        >
          <ContentComponent
            previewUrl={previewUrl}
            templateName={template.name}
            aspectRatio={aspectRatio}
            productName={productName}
          />
        </Box>
      )}
    </>
  )
}
